import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Bot, LayoutGrid, Radar, Server } from 'lucide-react';
import { useLang } from '../LanguageProvider';
import translations from '../../translations';

/**
 * YÖRÜNGE HARİTASI — dört WhowSell uygulaması ortadaki çekirdeğin
 * çevresinde döner; birine dokununca ne yaptığını anlatan kart açılır.
 *
 * Kaynak: radar-whowsell/components/cloud/orbital-map.tsx
 *         (crm / ai / server kopyalarıyla mantık olarak birebir aynı)
 *
 * SAPMALAR VE GEREKÇELERİ
 *  1. TypeScript -> saf JavaScript; `next/image` yok, `<img>` kullanıldı
 *     (zaten kaynak da `<img>` kullanıyordu).
 *  2. Sözlük: kaynak 7 dilli `useI18n().t('cloud.x')` çağırıyor. Burada
 *     iki dil var; `translations[lang].cloud` doğrudan okunuyor.
 *  3. Logo: kaynakta `/logo/whow_96x96.png` ve `/logo/whow_64x64.png`
 *     vardı; bu projede o dosyalar yok. En yakın ölçüdeki hazır varlıklar
 *     `public/logo/favicon-192x192.png` (42px kutu) ve
 *     `favicon-64x64.png` (24px kutu) seçildi. `whow.png` bilerek
 *     KULLANILMADI: 460 KB, bu ölçüler için gereksiz ağır.
 *  4. YENİ — `--wh-cloud-vw`: kaynakta kartın dar ekran genişliği
 *     `calc(100vw - 28px)` ile veriliyordu. `100vw` DİKEY KAYDIRMA
 *     ÇUBUĞUNU DA SAYAR; klasik (kaplamayan) kaydırma çubuğu olan
 *     masaüstü tarayıcılarda kart, kullanılabilir alandan çubuk genişliği
 *     kadar (Chromium'da 15px) taşıyordu. Kardeş projelerde kayda geçen
 *     "whowsell-cloud kart taşması" hatasının artık kalan tek kaynağı
 *     buydu. Burada gerçek iç genişlik (`documentElement.clientWidth`)
 *     bir CSS değişkenine yazılıyor ve genişlik ondan hesaplanıyor;
 *     çubuk varken de yokken de taşma matematiksel olarak imkânsız.
 */

const UYGULAMALAR = [
  {
    id: 'radar',
    ad: 'Radar',
    adres: 'https://radar.whowsell.com',
    Ikon: Radar,
    gradyan: 'linear-gradient(100deg, #7c3aed 0%, #6d28d9 30%, #d946ef 62%, #ec4899 100%)',
    baglar: ['CRM'],
  },
  {
    id: 'crm',
    ad: 'CRM',
    adres: 'https://crm.whowsell.com',
    Ikon: LayoutGrid,
    gradyan: 'linear-gradient(100deg, #fbbf24 0%, #f59e0b 38%, #f97316 72%, #ea580c 100%)',
    baglar: ['Radar', 'AI'],
  },
  {
    id: 'ai',
    ad: 'AI',
    adres: 'https://ai.whowsell.com',
    Ikon: Bot,
    gradyan: 'linear-gradient(100deg, #7c3aed 0%, #667eea 48%, #06b6d4 100%)',
    baglar: ['CRM'],
  },
  {
    id: 'server',
    ad: 'Server',
    adres: 'https://server.whowsell.com',
    Ikon: Server,
    gradyan: 'linear-gradient(90deg, #06b6d4, #10b981, #7c5cff, #06b6d4)',
    baglar: ['CRM'],
  },
];

export default function OrbitalMap() {
  const { lang } = useLang();
  const t = translations[lang].cloud;
  const sahneRef = useRef(null);
  const kartRef = useRef(null);
  const [aci, setAci] = useState(0);
  const [acik, setAcik] = useState(null);

  /* Gerçek iç genişliği (kaydırma çubuğu hariç) CSS'e taşır.
     Bkz. dosya başındaki 4. sapma notu. */
  useEffect(() => {
    const yaz = () => {
      document.documentElement.style.setProperty(
        '--wh-cloud-vw',
        `${document.documentElement.clientWidth}px`,
      );
    };
    yaz();
    window.addEventListener('resize', yaz);
    window.addEventListener('orientationchange', yaz);
    return () => {
      window.removeEventListener('resize', yaz);
      window.removeEventListener('orientationchange', yaz);
      document.documentElement.style.removeProperty('--wh-cloud-vw');
    };
  }, []);

  /* Dönüş. Kart açıkken durur — okunan kartın altındaki düğme kaymasın. */
  useEffect(() => {
    if (acik) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    let kare = 0;
    let son = performance.now();
    let canli = true;

    const tik = (simdi) => {
      if (simdi - son >= 40) {
        son = simdi;
        setAci((onceki) => (onceki + 0.3) % 360);
      }
      if (canli) kare = requestAnimationFrame(tik);
    };

    const gorunurluk = () => {
      canli = document.visibilityState === 'visible';
      if (canli && !kare) kare = requestAnimationFrame(tik);
      if (!canli && kare) {
        cancelAnimationFrame(kare);
        kare = 0;
      }
    };

    kare = requestAnimationFrame(tik);
    document.addEventListener('visibilitychange', gorunurluk);

    return () => {
      canli = false;
      if (kare) cancelAnimationFrame(kare);
      document.removeEventListener('visibilitychange', gorunurluk);
    };
  }, [acik]);

  /* Açılan kartı sahnenin ve ekranın içine oturtur. */
  useEffect(() => {
    if (!acik) return undefined;
    let raf = 0;

    const otur = () => {
      const kart = kartRef.current;
      const sahne = sahneRef.current;
      if (!kart || !sahne) return;

      kart.style.setProperty('--wh-dx', '0px');
      kart.style.setProperty('--wh-dy', '0px');
      kart.classList.remove('is-kaydi');

      const kutu = sahne.getBoundingClientRect();
      const olcu = kart.getBoundingClientRect();
      const pay = 10;
      const gorunen = document.documentElement.clientWidth;
      const sol = Math.max(pay, kutu.left + 2);
      const sag = Math.min(gorunen - pay, kutu.right - 2);

      let dx = 0;
      if (olcu.width >= sag - sol) dx = sol - olcu.left;
      else if (olcu.left < sol) dx = sol - olcu.left;
      else if (olcu.right > sag) dx = sag - olcu.right;

      let dy = 0;
      if (olcu.height >= kutu.height - 4) dy = kutu.top + 2 - olcu.top;
      else if (olcu.bottom > kutu.bottom - 2) dy = kutu.bottom - 2 - olcu.bottom;
      else if (olcu.top < kutu.top + 2) dy = kutu.top + 2 - olcu.top;

      if (dx) kart.style.setProperty('--wh-dx', `${Math.round(dx)}px`);
      if (dy) {
        kart.style.setProperty('--wh-dy', `${Math.round(dy)}px`);
        kart.classList.add('is-kaydi');
      }
    };

    const gorunurYap = () => {
      const kart = kartRef.current;
      if (!kart) return;
      const olcu = kart.getBoundingClientRect();
      const alan = window.innerHeight;
      const pay = 16;
      let fark = 0;
      if (olcu.bottom > alan - pay) fark = olcu.bottom - (alan - pay);
      else if (olcu.top < pay) fark = olcu.top - pay;
      if (!fark) return;
      const duragan =
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        document.visibilityState !== 'visible';
      window.scrollBy({ top: fark, behavior: duragan ? 'auto' : 'smooth' });
    };

    const calistir = () => {
      otur();
      gorunurYap();
    };

    raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(calistir);
    });
    const tetik = window.setTimeout(calistir, 60);
    const yerlesme = window.setTimeout(otur, 420);
    window.addEventListener('resize', otur);
    window.addEventListener('orientationchange', otur);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.clearTimeout(tetik);
      window.clearTimeout(yerlesme);
      window.removeEventListener('resize', otur);
      window.removeEventListener('orientationchange', otur);
    };
  }, [acik]);

  /* Sıradaki uygulamanın yörüngedeki yeri. `near` 0..1: elips üzerinde
     öne gelenin katman sırası ve saydamlığı artar, derinlik hissi buradan. */
  const koltuk = (sira) => {
    const toplam = UYGULAMALAR.length;
    const a = ((sira / toplam) * 360 + aci) % 360;
    const rad = (a * Math.PI) / 180;
    const on = (Math.sin(rad) + 1) / 2;
    return {
      x: Math.cos(rad),
      y: Math.sin(rad),
      z: Math.round(20 + 30 * on),
      o: 0.5 + on * 0.5,
    };
  };

  return (
    <div
      ref={sahneRef}
      className="wh-cloud-orbit"
      onClick={(olay) => {
        if (olay.target === sahneRef.current) setAcik(null);
      }}
    >
      <span className="wh-cloud-ring" aria-hidden="true" />

      <div className="wh-cloud-core">
        <span className="wh-cloud-core-mark">
          <img src="/logo/favicon-192x192.png" alt="" width={42} height={42} decoding="async" />
        </span>
        <span className="wh-cloud-core-name">WhowSell</span>
        <span className="wh-cloud-core-sub">Cloud</span>
      </div>

      {UYGULAMALAR.map((uygulama, sira) => {
        const yer = koltuk(sira);
        const etkin = acik === uygulama.id;
        const Ikon = uygulama.Ikon;
        return (
          <div
            key={uygulama.id}
            className={etkin ? 'wh-cloud-node on' : 'wh-cloud-node'}
            style={{
              transform: `translate(calc(${yer.x} * var(--wh-orbit-x) - 50%), calc(${yer.y} * var(--wh-orbit-y) - 50%))`,
              zIndex: etkin ? 60 : yer.z,
              opacity: etkin ? 1 : yer.o,
              '--wh-card-pull': `calc(${-yer.x} * var(--wh-orbit-x))`,
            }}
          >
            <button
              type="button"
              className="wh-cloud-dot"
              style={etkin ? { backgroundImage: uygulama.gradyan, borderColor: 'transparent' } : undefined}
              onClick={() => setAcik(etkin ? null : uygulama.id)}
              aria-expanded={etkin}
              aria-label={`WhowSell ${uygulama.ad}`}
            >
              <img src="/logo/favicon-64x64.png" alt="" width={24} height={24} decoding="async" />
              <Ikon size={21} />
            </button>

            <span
              className="wh-cloud-name"
              style={etkin ? { backgroundImage: uygulama.gradyan } : undefined}
            >
              {uygulama.ad}
            </span>

            {etkin ? (
              <div ref={kartRef} className={yer.y > -0.25 ? 'wh-cloud-card up' : 'wh-cloud-card'}>
                <span className="wh-cloud-card-stem" aria-hidden="true" />
                <div className="wh-cloud-card-top">
                  <span className="wh-cloud-live">{t.live}</span>
                  <span className="wh-cloud-host">{uygulama.adres.replace('https://', '')}</span>
                </div>
                <h3 className="wh-cloud-card-title" style={{ backgroundImage: uygulama.gradyan }}>
                  {`WhowSell ${uygulama.ad}`}
                </h3>
                <p className="wh-cloud-card-line">{t[`${uygulama.id}Line`]}</p>
                <p className="wh-cloud-card-text">{t[`${uygulama.id}Desc`]}</p>

                <div className="wh-cloud-card-rel">
                  <span className="wh-cloud-rel-head">{t.worksWith}</span>
                  <div className="wh-cloud-rel-list">
                    {uygulama.baglar.map((bag) => (
                      <button
                        key={bag}
                        type="button"
                        className="wh-cloud-rel-btn"
                        onClick={(olay) => {
                          olay.stopPropagation();
                          const sonraki = UYGULAMALAR.find((oge) => oge.ad === bag);
                          if (sonraki) setAcik(sonraki.id);
                        }}
                      >
                        {bag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Boyut `.wh-cta` sınıfından gelir: tek satıra kilitli,
                    clamp() ile orantılı küçülür. Renk App.css'te, nötr ve
                    tema duyarlı: kaynakta da bu bağlantı düz metindi.
                    Uygulamanın gradyanı BİLEREK kullanılmadı — amber ve mor
                    uçlarda üstündeki yazının karşıtlığı 3:1'in altına
                    düşüyordu (ölçüldü). Gradyan kartın başlığında zaten var. */}
                <a
                  className="wh-cta wh-cloud-go"
                  href={uygulama.adres}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t.visit}
                  <ArrowUpRight />
                </a>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
